import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import { refreshHolidayReview } from '../utils/holidayReview.js'
import './StandbySettings.css'

// Opening hours, lunch break and holidays for the kiosk's standby screen. The same
// schedule also drives the barriers, the ramp alerts and the Disposition grid — see
// server/services/siteSchedule.js and Plans/Standby_Erweiterungen_Plan.md.

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_NAMES = {
  mon: 'Montag', tue: 'Dienstag', wed: 'Mittwoch',
  thu: 'Donnerstag', fri: 'Freitag', sat: 'Samstag', sun: 'Sonntag',
}

const DEFAULT_HOURS = {
  mon: { start: '08:00', end: '18:00' },
  tue: { start: '08:00', end: '18:00' },
  wed: { start: '08:00', end: '18:00' },
  thu: { start: '08:00', end: '18:00' },
  fri: { start: '08:00', end: '17:00' },
  sat: null,
  sun: null,
}

const DEFAULT_BREAK = { start: '12:00', end: '13:00' }

const FEDERAL_STATES = [
  ['BW', 'Baden-Württemberg'], ['BY', 'Bayern'], ['BE', 'Berlin'], ['BB', 'Brandenburg'],
  ['HB', 'Bremen'], ['HH', 'Hamburg'], ['HE', 'Hessen'], ['MV', 'Mecklenburg-Vorpommern'],
  ['NI', 'Niedersachsen'], ['NW', 'Nordrhein-Westfalen'], ['RP', 'Rheinland-Pfalz'],
  ['SL', 'Saarland'], ['SN', 'Sachsen'], ['ST', 'Sachsen-Anhalt'], ['SH', 'Schleswig-Holstein'],
  ['TH', 'Thüringen'],
]

const localToday = () => new Date().toLocaleDateString('sv-SE') // YYYY-MM-DD
const thisYear = () => new Date().getFullYear()
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `h-${Date.now()}-${Math.random().toString(16).slice(2)}`)
const formatDate = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
const nameDe = (name) => (typeof name === 'string' ? name : name?.de || '')

/** Start/end time pair. */
function TimeRange({ value, onChange, disabled }) {
  return (
    <span className="ss-range">
      <input type="time" className="form-control ss-time" value={value?.start || ''} disabled={disabled}
        onChange={e => onChange({ ...value, start: e.target.value })} />
      <span className="ss-dash">–</span>
      <input type="time" className="form-control ss-time" value={value?.end || ''} disabled={disabled}
        onChange={e => onChange({ ...value, end: e.target.value })} />
    </span>
  )
}

/**
 * One day's hours: open/closed, times, and an optional lunch break. Used for the
 * weekdays and for a holiday's special hours alike.
 */
function DayHours({ value, onChange, onApplyBreakToAll }) {
  const closed = !value
  return (
    <div className="ss-day-hours">
      <label className="ss-inline">
        <span className="toggle">
          <input type="checkbox" checked={closed}
            onChange={e => onChange(e.target.checked ? null : { start: '08:00', end: '18:00' })} />
          <span className="toggle-slider" />
        </span>
        <span className="ss-muted">Geschlossen</span>
      </label>

      {!closed && (
        <>
          <TimeRange value={value} onChange={v => onChange({ ...value, start: v.start, end: v.end })} />
          <label className="ss-inline ss-break-toggle">
            <input type="checkbox" checked={!!value.break}
              onChange={e => onChange({ ...value, break: e.target.checked ? { ...DEFAULT_BREAK } : undefined })} />
            <span>Mittagspause</span>
          </label>
          {value.break && (
            <>
              <TimeRange value={value.break} onChange={b => onChange({ ...value, break: b })} />
              {onApplyBreakToAll && (
                <button type="button" className="ss-link" onClick={() => onApplyBreakToAll(value.break)}
                  title="Diese Mittagspause für alle geöffneten Tage übernehmen">
                  für alle Tage übernehmen
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default function StandbySettings() {
  const { apiFetch, admin, isOwner } = useAuth()

  // An owner arriving from the holiday toast lands on that tenant.
  const tenantFromUrl = isOwner ? new URLSearchParams(window.location.search).get('tenantId') : null

  const [tenants,  setTenants]  = useState([])
  const [tenantId, setTenantId] = useState(isOwner ? tenantFromUrl : admin?.tenant_id)
  const [enabled,  setEnabled]  = useState(false)
  const [phone,    setPhone]    = useState('')
  const [message,  setMessage]  = useState('')
  const [hours,    setHours]    = useState(DEFAULT_HOURS)
  const [holidays, setHolidays] = useState([])
  const [savedHolidays, setSavedHolidays] = useState('[]')   // JSON of the last saved list
  const [pending,  setPending]  = useState([])
  const [reviewBusy, setReviewBusy] = useState(null) // { date, action } while a decision is saved
  const [federalState, setFederalState] = useState(null)
  const [showPast, setShowPast] = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [msg,      setMsg]      = useState(null)
  const [loading,  setLoading]  = useState(false)

  // Import dialog
  const [importOpen,  setImportOpen]  = useState(false)
  const [importState, setImportState] = useState('BW')
  const [importYear,  setImportYear]  = useState(thisYear())
  const [preview,     setPreview]     = useState(null)    // [{ date, name, note }]
  const [selected,    setSelected]    = useState(new Set())
  const [importBusy,  setImportBusy]  = useState(false)
  const [importError, setImportError] = useState(null)

  const qs = isOwner ? `?tenantId=${tenantId}` : ''
  const holidaysDirty = JSON.stringify(holidays) !== savedHolidays

  function flash(type, text) {
    setMsg({ type, text })
    setTimeout(() => setMsg(null), type === 'error' ? 8000 : 4000)
  }

  // Takes the server's view of the holiday fields — after load, save, import, review.
  function applyHolidayState(standby) {
    const list = standby.holidays || []
    setHolidays(list)
    setSavedHolidays(JSON.stringify(list))
    setPending(standby.holidaysPending || [])
    setFederalState(standby.federalState || null)
    if (standby.federalState) setImportState(standby.federalState)
  }

  // Load tenant list for owners
  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data)
          setTenantId(current => (current && data.some(t => t.id === current) ? current : data[0].id))
        }
      })
      .catch(() => {})
  }, [isOwner])

  // Load standby config whenever tenantId changes
  useEffect(() => {
    if (!tenantId) return
    setLoading(true)
    setImportOpen(false)
    apiFetch(`/api/admin/standby-settings${qs}`)
      .then(r => r.json())
      .then(f => {
        setEnabled(f.enabled === true)
        setPhone(f.phone || '')
        // message may be a { de, en, ... } object if previously translated
        setMessage(typeof f.message === 'object' && f.message !== null
          ? (f.message.de || '')
          : (f.message || ''))
        setHours(f.hours || DEFAULT_HOURS)
        applyHolidayState(f)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId])

  // ── Weekdays ──────────────────────────────────────────────────────────────
  function setDay(key, value) {
    setHours(prev => ({ ...prev, [key]: value }))
  }

  function applyBreakToAll(brk) {
    setHours(prev => Object.fromEntries(DAY_ORDER.map(key => [
      key, prev[key] ? { ...prev[key], break: { ...brk } } : prev[key] ?? null,
    ])))
  }

  // ── Holidays ──────────────────────────────────────────────────────────────
  function updateHoliday(id, patch) {
    setHolidays(prev => prev.map(h => (h.id === id ? { ...h, ...patch } : h)))
  }

  function toggleYearly(h, yearly) {
    if (yearly) {
      updateHoliday(h.id, { monthDay: (h.date || `${thisYear()}-01-01`).slice(5), date: undefined })
    } else {
      updateHoliday(h.id, { date: `${thisYear()}-${h.monthDay || '01-01'}`, monthDay: undefined })
    }
  }

  function addHoliday() {
    setHolidays(prev => [...prev, { id: newId(), date: localToday(), name: { de: '' }, hours: null, source: 'manual' }])
    setShowPast(true)
  }

  function removeHoliday(id) {
    setHolidays(prev => prev.filter(h => h.id !== id))
  }

  const today = localToday()
  const visibleHolidays = holidays.filter(h => showPast || !h.date || h.date >= today)
  const hiddenPast = holidays.length - visibleHolidays.length

  // ── Import ────────────────────────────────────────────────────────────────
  async function loadPreview() {
    setImportBusy(true)
    setImportError(null)
    setPreview(null)
    try {
      const res = await apiFetch(`/api/admin/holidays/preview?state=${importState}&year=${importYear}${isOwner ? `&tenantId=${tenantId}` : ''}`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler')
      const known = new Set(holidays.map(h => h.date).filter(Boolean))
      setPreview(data.holidays)
      // Pre-selected: everything new without a note. A note means "only in part of the
      // state" or "one-off" — those are for the admin to decide, not to tick by default.
      setSelected(new Set(data.holidays.filter(h => !h.note && !known.has(h.date)).map(h => h.date)))
    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportBusy(false)
    }
  }

  async function runImport() {
    setImportBusy(true)
    setImportError(null)
    try {
      const res = await apiFetch(`/api/admin/holidays/import${qs}`, {
        method: 'POST',
        body: JSON.stringify({ state: importState, year: importYear, selectedDates: [...selected] }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler')
      applyHolidayState(data.standby)
      setImportOpen(false)
      setPreview(null)
      refreshHolidayReview(apiFetch)
      flash('success', `${selected.size} Feiertag(e) übernommen.`)
    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportBusy(false)
    }
  }

  // ── Review ("zu prüfen") ──────────────────────────────────────────────────
  async function review(date, action) {
    if (reviewBusy) return
    setReviewBusy({ date, action })
    try {
      const res = await apiFetch(`/api/admin/holidays/review${qs}`, {
        method: 'POST',
        body: JSON.stringify({ date, action }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler')
      // Keep unsaved edits to the list; take only what the review changed.
      const accepted = (data.standby.holidays || []).find(h => h.date === date)
      if (action === 'accept' && accepted) {
        setHolidays(prev => (prev.some(h => h.date === date) ? prev : [...prev, accepted]))
        setSavedHolidays(prev => JSON.stringify([...JSON.parse(prev), accepted]))
      }
      setPending(data.standby.holidaysPending || [])
      refreshHolidayReview(apiFetch)
    } catch (err) {
      flash('error', `Fehler: ${err.message}`)
    } finally {
      setReviewBusy(null)
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true)
    setMsg(null)
    try {
      const res = await apiFetch(`/api/admin/standby-settings${qs}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled, phone, message, hours, holidays }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Unbekannter Fehler')
      if (data.standby) {
        setHours(data.standby.hours || {})
        applyHolidayState(data.standby)
      }
      refreshHolidayReview(apiFetch)
      flash('success', 'Einstellungen gespeichert.')
    } catch (err) {
      flash('error', `Fehler beim Speichern: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading && !isOwner) return <div className="loading-state"><span className="spinner" /> Laden…</div>

  const knownDates = new Set(holidays.map(h => h.date).filter(Boolean))

  return (
    <div>
      <div className="page-header">
        <h1>Standby-Bildschirm</h1>
        <p>Außerhalb der Öffnungszeiten, in der Mittagspause und an Feiertagen zeigt das Kiosk automatisch einen Geschlossen-Bildschirm.</p>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card ss-card">
          <div className="form-group ss-flush">
            <label>Mandant</label>
            <select
              className="form-control"
              value={tenantId || ''}
              onChange={e => setTenantId(e.target.value)}
            >
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {msg.text}
        </div>
      )}

      {/* Enable toggle */}
      <div className="card ss-card">
        <div className="row-between">
          <div>
            <strong>Standby-Bildschirm aktivieren</strong>
            <p className="ss-hint">
              Kiosk wechselt außerhalb der Öffnungszeiten automatisch in den Standby-Modus.
            </p>
          </div>
          <label className="toggle">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <span className="toggle-slider" />
          </label>
        </div>
      </div>

      {/* Opening hours */}
      <div className="card ss-card">
        <p className="section-title">Öffnungszeiten</p>
        <div className="ss-week">
          {DAY_ORDER.map(key => (
            <div key={key} className="ss-week-row">
              <span className="ss-day-name">{DAY_NAMES[key]}</span>
              <DayHours value={hours[key] ?? null} onChange={v => setDay(key, v)} onApplyBreakToAll={applyBreakToAll} />
            </div>
          ))}
        </div>
        <p className="form-hint">
          Während der Mittagspause zeigt das Kiosk „Mittagspause – wieder geöffnet ab …". Schranken verhalten
          sich wie außerhalb der Öffnungszeiten, Zielort-Hinweise werden bis nach der Pause zurückgehalten.
        </p>
      </div>

      {/* Holidays */}
      <div className="card ss-card">
        <div className="section-title ss-title-row">
          <span>Feiertage</span>
          {federalState && (
            <span className="badge badge-blue" title="Wird jährlich automatisch aktualisiert">
              {FEDERAL_STATES.find(([code]) => code === federalState)?.[1] ?? federalState}
            </span>
          )}
        </div>

        {pending.length > 0 && (
          <div className="ss-review">
            <p className="ss-review-title">📅 Zu prüfen ({pending.length})</p>
            <p className="ss-hint">
              Neu gefunden — gilt nur in Teilen des Landes, einmalig oder wurde nachträglich beschlossen.
              Bitte entscheiden, ob der Tag für Ihren Standort gilt.
            </p>
            {pending.map(p => (
              <div key={p.date} className="ss-review-row">
                <div className="ss-review-text">
                  <strong>{formatDate(p.date)} — {p.name}</strong>
                  {p.note && <span className="ss-note">{p.note}</span>}
                </div>
                <div className="ss-review-actions">
                  {/* One decision at a time: both buttons are off while any request runs. */}
                  <button className="btn btn-primary btn-sm" disabled={!!reviewBusy} onClick={() => review(p.date, 'accept')}>
                    {reviewBusy?.date === p.date && reviewBusy.action === 'accept' ? 'Wird übernommen…' : 'Übernehmen'}
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={!!reviewBusy} onClick={() => review(p.date, 'dismiss')}>
                    {reviewBusy?.date === p.date && reviewBusy.action === 'dismiss' ? 'Wird verworfen…' : 'Verwerfen'}
                  </button>
                  {reviewBusy?.date === p.date && <span className="spinner spinner-sm" />}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="ss-info">
          <p>Gesetzliche Feiertage werden aus einer öffentlichen Quelle (feiertage-api.de) übernommen und jedes Jahr
            ab 1. Dezember automatisch für das Folgejahr ergänzt. Bitte prüfen Sie selbst und tragen Sie bei Bedarf von Hand ein:</p>
          <ul>
            <li>Feiertage, die nur in einzelnen Gemeinden gelten (z. B. Mariä Himmelfahrt in Bayern, Fronleichnam in Teilen
              Sachsens und Thüringens, Augsburger Friedensfest),</li>
            <li>kurzfristig beschlossene einmalige Feiertage — sie werden monatlich gesucht und oben als „zu prüfen" angezeigt,
              sobald die Quelle sie kennt,</li>
            <li>betriebliche Schließtage (Betriebsurlaub, Inventur, 24.12./31.12.).</li>
          </ul>
        </div>

        {/* Import */}
        {!importOpen ? (
          <div className="ss-import-bar">
            <button className="btn btn-ghost" onClick={() => { setImportOpen(true); setPreview(null); setImportError(null) }}
              disabled={holidaysDirty}>
              Gesetzliche Feiertage übernehmen…
            </button>
            {holidaysDirty && <span className="ss-hint">Erst die Änderungen an der Liste speichern.</span>}
          </div>
        ) : (
          <div className="ss-import">
            <div className="ss-import-controls">
              <select className="form-control" value={importState} onChange={e => { setImportState(e.target.value); setPreview(null) }}>
                {FEDERAL_STATES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <select className="form-control ss-year" value={importYear} onChange={e => { setImportYear(Number(e.target.value)); setPreview(null) }}>
                {[thisYear(), thisYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button className="btn btn-ghost" onClick={loadPreview} disabled={importBusy}>
                {importBusy && !preview ? 'Laden…' : 'Vorschau laden'}
              </button>
              <button className="btn btn-ghost" onClick={() => setImportOpen(false)}>Abbrechen</button>
            </div>

            {importError && <div className="alert alert-error ss-import-error">{importError}</div>}

            {preview && (
              <>
                <div className="ss-preview">
                  {preview.map(h => {
                    const exists = knownDates.has(h.date)
                    return (
                      <label key={h.date} className={`ss-preview-row${exists ? ' ss-preview-row--exists' : ''}`}>
                        <input type="checkbox" disabled={exists} checked={!exists && selected.has(h.date)}
                          onChange={e => setSelected(prev => {
                            const next = new Set(prev)
                            if (e.target.checked) next.add(h.date); else next.delete(h.date)
                            return next
                          })} />
                        <span className="ss-preview-text">
                          <span>{formatDate(h.date)} — {h.name}{exists ? ' (bereits vorhanden)' : ''}</span>
                          {h.note && !exists && (
                            <span className="ss-note">{h.note} — trifft das auf Ihren Standort zu?</span>
                          )}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <div className="ss-import-controls">
                  <button className="btn btn-primary" onClick={runImport} disabled={importBusy}>
                    {importBusy ? 'Übernehmen…' : `${selected.size} Feiertag(e) übernehmen`}
                  </button>
                  <span className="ss-hint">Nicht ausgewählte Einträge werden nicht erneut vorgeschlagen.</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* The list */}
        <div className="ss-holidays">
          {visibleHolidays.length === 0 && (
            <p className="ss-hint">Keine {hiddenPast ? 'kommenden ' : ''}Feiertage eingetragen.</p>
          )}
          {visibleHolidays.map(h => {
            const yearly = !!h.monthDay
            return (
              <div key={h.id} className="ss-holiday">
                <div className="ss-holiday-head">
                  <input type="date" className="form-control ss-date"
                    value={yearly ? `${thisYear()}-${h.monthDay}` : (h.date || '')}
                    onChange={e => updateHoliday(h.id, yearly
                      ? { monthDay: e.target.value.slice(5) }
                      : { date: e.target.value })} />
                  <label className="ss-inline">
                    <input type="checkbox" checked={yearly} onChange={e => toggleYearly(h, e.target.checked)} />
                    <span>jährlich</span>
                  </label>
                  <input type="text" className="form-control ss-name" placeholder="Name, z. B. Betriebsurlaub"
                    value={nameDe(h.name)}
                    onChange={e => updateHoliday(h.id, { name: { ...(typeof h.name === 'object' ? h.name : {}), de: e.target.value } })} />
                  <button className="btn btn-danger btn-sm" onClick={() => removeHoliday(h.id)} title="Entfernen">✕</button>
                </div>
                <DayHours value={h.hours ?? null} onChange={v => updateHoliday(h.id, { hours: v })} />
                {h.note && <span className="ss-note">Hinweis der Quelle: {h.note}</span>}
              </div>
            )
          })}
        </div>

        <div className="ss-list-actions">
          <button className="btn btn-ghost" onClick={addHoliday}>+ Feiertag / Schließtag hinzufügen</button>
          {hiddenPast > 0 && (
            <button className="ss-link" onClick={() => setShowPast(s => !s)}>
              {showPast ? 'Vergangene ausblenden' : `${hiddenPast} vergangene anzeigen`}
            </button>
          )}
        </div>
        <p className="form-hint">
          Namen auf Deutsch eingeben — sie werden beim Speichern automatisch in alle Kiosk-Sprachen übersetzt.
          An einem Feiertag gelten dessen Zeiten statt der Wochentagszeiten, auch für die Schranken.
        </p>
      </div>

      {/* Phone + message */}
      <div className="card ss-card">
        <p className="section-title">Kontakt &amp; Nachricht</p>
        <div className="form-group">
          <label>Telefonnummer (außerhalb der Öffnungszeiten)</label>
          <input
            type="tel"
            className="form-control"
            placeholder="+49 xxx xxxxxxx"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
        </div>
        <div className="form-group ss-flush">
          <label>Nachricht auf dem Standby-Bildschirm</label>
          <input
            type="text"
            className="form-control"
            placeholder="Zurzeit außerhalb der Öffnungszeiten"
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
          <p className="form-hint">Auf Deutsch eingeben — wird beim Speichern automatisch in alle 26 Sprachen übersetzt.
            In der Mittagspause und an Feiertagen zeigt das Kiosk stattdessen einen passenden Hinweis.</p>
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Speichern…' : 'Speichern'}
      </button>
    </div>
  )
}
